import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { date } from '@ipfabric/utils';
import { Button } from 'components/Button/Button';
import { OverflowMenu } from 'components/DropdownMenu/OverflowMenu';
import { OverflowMenuButtonLegacy } from 'components/DropdownMenu/OverflowMenuButton';
import { JOB_ACTIONS } from 'containers/App/Header/JobManagement/constants';
import {
  makeSelectDiscoveryIsInJobs,
  makeSelectDiscoveryRunByUser,
  makeSelectDiscoveryStarting,
  makeSelectScheduledClone,
  makeSelectScheduledDelete,
  makeSelectScheduledLoading,
  makeSelectScheduledSnapshotDownload,
  makeSelectScheduledUnloading,
  makeSelectSnapshotDownloadRunning,
  makeSelectSnapshotIsInJobs,
} from 'containers/App/Header/JobManagement/selectors';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { toggleConfirmDialog } from 'containers/ConfirmDialog/actions';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { merge } from 'ramda';
import { PureComponent } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import handleEvent from 'utils/handleEvent';
import { withAppProps } from 'utils/react/withAppProps';
import {
  cloneSnapshot,
  deleteSnapshot,
  discoveryProcessStart,
  discoveryProcessStop,
  downloadSnapshot,
  loadSnapshot,
  lockSnapshot,
  openNewSettings,
  openSettings,
  runTopologyCalculation,
  unloadSnapshot,
  unlockSnapshot,
  uploadSnapshot,
} from './actions';
import {
  CANCEL_DISCOVERY_JOB_DIALOGUE_NAME,
  DELETE_DIALOGUE_NAME,
  SNAPSHOT_LOADED,
  SNAPSHOT_UNLOADED,
} from './constants';
import { isSnapshotLoaded, isSnapshotUnloaded } from './helpers';
import {
  makeSelectAllSnapshots,
  makeSelectCloningSnapshot,
  makeSelectContentSnapshot,
  makeSelectDeletingSnapshot,
  makeSelectDisableStartBtn,
  makeSelectLoadingSnapshot,
  makeSelectLockingSnapshot,
  makeSelectSettingsLoading,
  makeSelectUnloadingSnapshot,
  makeSelectUnlockingSnapshot,
  makeSelectUploadingSnapshot,
} from './selectors';

/**
 * getSnapshots() on the backend remaps an unloaded snapshot's `status` to
 * `finishStatus` (see packages/backend-shared/src/lib/models/snapshot.ts),
 * so an unloaded-but-was-canceled snapshot arrives here with
 * `status: 'canceled'` even though it is currently unloaded. This means
 * `status === 'canceled'` alone cannot distinguish a loaded-canceled
 * snapshot from an unloaded-canceled one - only the `loaded` flag can.
 */
export function isSnapshotCanceled(snapshot) {
  return snapshot?.get('status') === 'canceled';
}

export function isRunTopologyCalculationDisabled({
  loaded,
  discoveryStarting,
  isRunning,
  isLoading,
  hasWritePermission,
  snapshotIsInJobs,
  id,
  topologyCalculationAlreadyRun,
}) {
  return (
    !loaded ||
    discoveryStarting ||
    isRunning ||
    isLoading ||
    !hasWritePermission ||
    snapshotIsInJobs ||
    id === null ||
    Boolean(topologyCalculationAlreadyRun)
  );
}

@withAppProps
class Buttons extends PureComponent {
  static propTypes = {
    auth: PropTypes.object.isRequired,
    cloningSnapshot: ImmutablePropTypes.list.isRequired,
    currentSnapshotId: ImmutablePropTypes.list.isRequired,
    dateTimeFormat: PropTypes.string.isRequired,
    deletingSnapshot: ImmutablePropTypes.list.isRequired,
    discoveryRunByUser: PropTypes.bool.isRequired,
    discoveryStarting: PropTypes.bool.isRequired,
    hasWritePermission: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool,
    isRunning: PropTypes.bool,
    isStartBtnDisabled: PropTypes.bool.isRequired,
    jobs: ImmutablePropTypes.list,
    loadingSnapshot: ImmutablePropTypes.list.isRequired,
    location: PropTypes.object,
    lockingSnapshot: ImmutablePropTypes.list.isRequired,
    msg: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    onClearSitesFilter: PropTypes.func.isRequired,
    onCloneSnapshot: PropTypes.func.isRequired,
    onDeleteSnapshots: PropTypes.func.isRequired,
    onDiscoveryProcessStart: PropTypes.func.isRequired,
    onDiscoveryProcessStop: PropTypes.func.isRequired,
    onDownloadSnapshot: PropTypes.func.isRequired,
    onLoadSnapshots: PropTypes.func.isRequired,
    onLockSnapshot: PropTypes.func.isRequired,
    onOpenNewSettings: PropTypes.func.isRequired,
    onOpenSettings: PropTypes.func.isRequired,
    onRunTopologyCalculation: PropTypes.func.isRequired,
    onToggleConfirmDialog: PropTypes.func.isRequired,
    onUnloadSnapshots: PropTypes.func.isRequired,
    onUnlockSnapshot: PropTypes.func.isRequired,
    params: PropTypes.object,
    scheduledClone: PropTypes.bool.isRequired,
    scheduledDelete: PropTypes.bool.isRequired,
    scheduledLoading: PropTypes.bool.isRequired,
    scheduledSnapshotDownload: PropTypes.bool.isRequired,
    scheduledUnloading: PropTypes.bool.isRequired,
    settingsLoading: PropTypes.bool.isRequired,
    showSettings: PropTypes.bool.isRequired,
    snapshot: ImmutablePropTypes.map,
    snapshotIsInJobs: PropTypes.bool.isRequired,
    snapshotRunning: PropTypes.bool.isRequired,
    snapshots: ImmutablePropTypes.list.isRequired,
    unloadingSnapshot: ImmutablePropTypes.list.isRequired,
    unlockingSnapshot: ImmutablePropTypes.list.isRequired,
    snapshotDownloadRunning: PropTypes.bool.isRequired,
    timezone: PropTypes.string.isRequired,
  };

  getDeleteConfirmDialogMessages = () => {
    const { currentSnapshotId, msg, dateTimeFormat, timezone, snapshots } =
      this.props;
    if (!snapshots || !snapshots.size || !currentSnapshotId.size) {
      return {};
    }
    if (currentSnapshotId.size > 1) {
      return {
        header: get(msg, 'deleteSnapshotDialogue.headerMulti', '').replace(
          '{totalSnapshots}',
          currentSnapshotId.size,
        ),
        subHeader: get(msg, 'deleteSnapshotDialogue.subHeaderMulti', ''),
        btnConfirm: get(msg, 'deleteSnapshotDialogue.btnConfirm'),
        btnSecondaryConfirm: get(msg, 'deleteSnapshotDialogue.btnCancel'),
      };
    }
    const foundRow = snapshots.find(
      (row) => row.get('id') === currentSnapshotId.get(0),
    );
    if (!foundRow) {
      return {};
    }
    const name = foundRow.get('name');
    return {
      header:
        typeof name !== 'string' || name.trim() === ''
          ? get(msg, 'deleteSnapshotDialogue.header', '').replace(
              '{tsStart}',
              date.formatUtcTimestampInTimeZone(
                foundRow.get('tsStart'),
                timezone,
                dateTimeFormat,
              ),
            )
          : get(msg, 'deleteSnapshotDialogue.headerName', '').replace(
              '{name}',
              `"${name}"`,
            ),
      subHeader: get(msg, 'deleteSnapshotDialogue.subHeader', ''),
      btnConfirm: get(msg, 'deleteSnapshotDialogue.btnConfirm'),
      btnSecondaryConfirm: get(msg, 'deleteSnapshotDialogue.btnCancel'),
    };
  };

  getCancelDialogMessages = (action) => {
    const { msg } = this.props;
    if (action === JOB_ACTIONS.snapshotLoad) {
      return {
        header: get(msg, 'cancelSnapshotLoadDialogue.header'),
        subHeader: get(msg, 'cancelSnapshotLoadDialogue.subHeader'),
        btnConfirm: get(msg, 'cancelSnapshotLoadDialogue.btnConfirm'),
        btnSecondaryConfirm: get(
          msg,
          'cancelSnapshotLoadDialogue.btnSecondaryConfirm',
        ),
      };
    }
    if (action === JOB_ACTIONS.topologyCalculation) {
      return {
        header: get(msg, 'cancelTopologyCalculationDialogue.header'),
        subHeader: get(msg, 'cancelTopologyCalculationDialogue.subHeader'),
        btnConfirm: get(msg, 'cancelTopologyCalculationDialogue.btnConfirm'),
        btnSecondaryConfirm: get(
          msg,
          'cancelTopologyCalculationDialogue.btnSecondaryConfirm',
        ),
      };
    }
    return {
      header: get(msg, 'cancelDiscoveryDialogue.header'),
      subHeader: get(msg, 'cancelDiscoveryDialogue.subHeader'),
      btnConfirm: get(msg, 'cancelDiscoveryDialogue.btnConfirm'),
      btnSecondaryConfirm: get(
        msg,
        'cancelDiscoveryDialogue.btnSecondaryConfirm',
      ),
    };
  };

  getActiveSnapshotHandlingJob = (status) => {
    const { jobs } = this.props;
    return jobs.find(
      (job) =>
        [
          JOB_ACTIONS.discovery,
          JOB_ACTIONS.snapshotLoad,
          JOB_ACTIONS.topologyCalculation,
        ].includes(job.get('action')) && job.get('status') === status,
    );
  };

  getMultiActions = () => {
    const {
      currentSnapshotId,
      deletingSnapshot,
      discoveryStarting,
      hasWritePermission,
      loadingSnapshot,
      msg,
      snapshotIsInJobs,
      snapshots,
      unloadingSnapshot,
    } = this.props;
    const allDeleted = currentSnapshotId.reduce((acc, snapshotId) => {
      const foundSnapshot = snapshots.find(
        (snapshot) => snapshot.get('id') === snapshotId,
      );
      if (foundSnapshot) {
        return false;
      }
      return acc;
    }, true);
    const allLoaded = currentSnapshotId.reduce((acc, snapshotId) => {
      const foundSnapshot = snapshots.find(
        (snapshot) => snapshot.get('id') === snapshotId,
      );
      return (foundSnapshot &&
        foundSnapshot.get('state') === SNAPSHOT_LOADED) ||
        snapshotIsInJobs
        ? acc
        : false;
    }, true);
    const allUnloaded = currentSnapshotId.reduce((acc, snapshotId) => {
      const foundSnapshot = snapshots.find(
        (snapshot) => snapshot.get('id') === snapshotId,
      );
      return (foundSnapshot &&
        (foundSnapshot.get('state') === SNAPSHOT_UNLOADED ||
          foundSnapshot.get('locked'))) ||
        snapshotIsInJobs
        ? acc
        : false;
    }, true);
    return (
      <div className='mb-lgu-5 flex'>
        <ConfirmDialog
          id={DELETE_DIALOGUE_NAME}
          onClickConfirm={this.handleDeleteSnapshotConfirm}
          msg={this.getDeleteConfirmDialogMessages()}
          btnConfirmVariant='primary'
          btnConfirmAccent='negative'
          btnCancelVariant='secondary'
          btnCancelAccent='positive'
        />
        <Button
          disabled={
            !!(
              allDeleted ||
              discoveryStarting ||
              loadingSnapshot.size ||
              unloadingSnapshot.size ||
              deletingSnapshot.size ||
              !hasWritePermission ||
              allLoaded
            )
          }
          variant='secondary'
          loading={loadingSnapshot.size > 0}
          id='snapshot-management-load'
          onClick={this.handleLoadSnapshots}
          type='button'
          className='relative mr-2'
          icon='fa-database'
        >
          <Icon
            glyph='fa-arrow-up'
            className='snapshots-buttons__button-icon'
            size='xs'
          />
          {get(msg, ['buttonToolbar', 'loadSnapshots'])}
        </Button>
        <Button
          variant='secondary'
          disabled={
            !!(
              allDeleted ||
              discoveryStarting ||
              loadingSnapshot.size ||
              unloadingSnapshot.size ||
              deletingSnapshot.size ||
              !hasWritePermission ||
              allUnloaded
            )
          }
          id='snapshot-management-unload'
          onClick={this.handleUnloadSnapshots}
          type='button'
          className='relative mr-2'
          loading={unloadingSnapshot.size > 0}
          icon='fa-database'
        >
          <Icon
            glyph='fa-arrow-down'
            className='snapshots-buttons__button-icon'
            size='xs'
          />
          {get(msg, ['buttonToolbar', 'unloadSnapshots'])}
        </Button>
        <Button
          variant='secondary'
          accent='negative'
          disabled={
            !!(
              allDeleted ||
              discoveryStarting ||
              deletingSnapshot.size ||
              !hasWritePermission
            )
          }
          id=''
          onClick={this.handleDeleteSnapshots}
          type='button'
          icon='fa-trash-can'
          loading={deletingSnapshot.size > 0}
        >
          {get(msg, ['buttonToolbar', 'deleteSnapshots'])}
        </Button>
      </div>
    );
  };

  handleDeleteSnapshots = handleEvent((e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    this.props.onToggleConfirmDialog(DELETE_DIALOGUE_NAME);
  });

  handleDeleteSnapshotConfirm = handleEvent(() => {
    const { snapshots, currentSnapshotId } = this.props;
    const data = snapshots.reduce((acc, item) => {
      if (currentSnapshotId.includes(item.get('id'))) {
        acc.push({
          id: item.get('id'),
          tsStart: item.get('tsStart'),
          name: item.get('name'),
        });
      }
      return acc;
    }, []);
    this.props.onDeleteSnapshots({ data });
  });

  handleLoadSnapshots = handleEvent((e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    const { snapshots, onLoadSnapshots, currentSnapshotId } = this.props;
    const data = snapshots.reduce((acc, item) => {
      if (
        isSnapshotUnloaded(item.get('state')) &&
        currentSnapshotId.includes(item.get('id'))
      ) {
        acc.push({
          id: item.get('id'),
          tsStart: item.get('tsStart'),
          name: item.get('name'),
        });
      }
      return acc;
    }, []);
    onLoadSnapshots({ data });
  });

  handleUnloadSnapshots = handleEvent((e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    const { snapshots, onUnloadSnapshots, currentSnapshotId } = this.props;
    const data = snapshots.reduce((acc, item) => {
      if (
        isSnapshotLoaded(item.get('state')) &&
        currentSnapshotId.includes(item.get('id'))
      ) {
        acc.push({
          id: item.get('id'),
          tsStart: item.get('tsStart'),
          name: item.get('name'),
        });
      }
      return acc;
    }, []);
    onUnloadSnapshots({ data });
  });

  makeHandleLock = (id) => (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    this.props.onLockSnapshot({ id });
  };

  makeHandleUnlock = (id) => (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    this.props.onUnlockSnapshot({ id });
  };

  makeHandleDownload = (id) => (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    this.props.onDownloadSnapshot({ id });
  };

  makeHandleRunTopologyCalculation = (id) => (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    this.props.onRunTopologyCalculation({ id });
  };

  makeHandleClone = (id) => (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    const { snapshots } = this.props;
    const snapshot = snapshots.find((item) => {
      return item.get('id') === id;
    });
    if (!snapshot) {
      return;
    }
    this.props.onCloneSnapshot({
      id: snapshot.get('id'),
      tsStart: snapshot.get('tsStart'),
      name: snapshot.get('name'),
    });
  };

  handleConnectionReport = handleEvent(() => {
    this.props.navigate('/snapshot-management/tasks');
  });

  handleNewSnapshot = handleEvent(() => {
    const { onClearSitesFilter, onDiscoveryProcessStart } = this.props;
    onClearSitesFilter();
    onDiscoveryProcessStart({ requestData: { action: 'new' } });
  });

  handleOpenNewSettings = handleEvent((e) => {
    const { onOpenNewSettings, location } = this.props;
    if (e?.preventDefault) {
      e.preventDefault();
    }
    onOpenNewSettings({ location });
    document.activeElement.blur();
    return false;
  });

  handleOpenSettings = handleEvent((e) => {
    const { onOpenSettings, location } = this.props;
    if (e?.preventDefault) {
      e.preventDefault();
    }
    document.activeElement.blur();
    onOpenSettings({ location });
    return false;
  });

  makeHandleStopButtonClick = (stopJobId) => (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    this.stopJobId = stopJobId;
    this.props.onToggleConfirmDialog(CANCEL_DISCOVERY_JOB_DIALOGUE_NAME);
  };

  handleStopButtonConfirm = handleEvent(() => {
    const { onDiscoveryProcessStop } = this.props;
    const jobId = this.stopJobId;
    onDiscoveryProcessStop(jobId);
  });

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: hic sunt dracones
  render() {
    const {
      auth,
      cloningSnapshot,
      currentSnapshotId,
      deletingSnapshot,
      discoveryRunByUser,
      discoveryStarting,
      hasWritePermission,
      isLoading,
      isRunning,
      isStartBtnDisabled,
      loadingSnapshot,
      lockingSnapshot,
      msg,
      params,
      scheduledClone,
      scheduledDelete,
      scheduledLoading,
      scheduledSnapshotDownload,
      scheduledUnloading,
      settingsLoading,
      showSettings,
      snapshot,
      snapshotIsInJobs,
      snapshotRunning,
      snapshots,
      unloadingSnapshot,
      unlockingSnapshot,
      snapshotDownloadRunning,
    } = this.props;
    if (currentSnapshotId.size > 1) {
      return this.getMultiActions(snapshots);
    }
    const hasSettingsPermission = auth.scope.includes('settings');
    const loaded = snapshot && isSnapshotLoaded(snapshot.get('state'));
    const locked = snapshot?.get('locked');
    const id = snapshot?.get('id');
    const isCanceled = isSnapshotCanceled(snapshot);
    const showRunTopologyCalculation = isCanceled;
    const topologyCalculationAlreadyRun = snapshot?.get(
      'topologyCalculationAlreadyRun',
    );
    const lockingOrUnlocking =
      lockingSnapshot.indexOf(id) > -1 || unlockingSnapshot.indexOf(id) > -1;
    const loadingOrUnloading =
      loadingSnapshot.indexOf(id) > -1 || unloadingSnapshot.indexOf(id) > -1;
    const cloning = cloningSnapshot.indexOf(id) > -1;
    const deleting = deletingSnapshot.indexOf(id) > -1;

    const newSnapshot = get(params, 'snapshotId') === 'new';
    const activeJob = this.getActiveSnapshotHandlingJob('running');
    const activeJobAction = activeJob?.get('action');
    const activeStoppingJob = this.getActiveSnapshotHandlingJob('stopping');
    const isActiveJobStopping = !!activeStoppingJob;
    const stopButtonsDisabled = activeJob ? !activeJob.get('stoppable') : true;
    const startButtonDisabled =
      discoveryStarting ||
      isStartBtnDisabled ||
      !hasWritePermission ||
      discoveryRunByUser ||
      settingsLoading ||
      showSettings;
    const startButtonSpinning =
      discoveryStarting || isStartBtnDisabled || discoveryRunByUser;
    return (
      <div className='flex'>
        <ConfirmDialog
          id={DELETE_DIALOGUE_NAME}
          onClickConfirm={this.handleDeleteSnapshotConfirm}
          msg={this.getDeleteConfirmDialogMessages()}
          btnConfirmVariant='primary'
          btnConfirmAccent='negative'
          btnCancelVariant='secondary'
          btnCancelAccent='positive'
        />
        {newSnapshot || snapshotRunning ? (
          <ConfirmDialog
            id={CANCEL_DISCOVERY_JOB_DIALOGUE_NAME}
            onClickConfirm={this.handleStopButtonConfirm}
            msg={this.getCancelDialogMessages(activeJobAction)}
            btnConfirmVariant='primary'
            btnConfirmAccent='negative'
            btnCancelVariant='secondary'
            btnCancelAccent='positive'
          />
        ) : null}
        {newSnapshot && !snapshotRunning ? (
          <div className='z-0'>
            <Button
              variant='primary'
              disabled={startButtonDisabled}
              onClick={this.handleNewSnapshot}
              type='button'
              icon='fa-play'
              loading={startButtonSpinning}
              className='mr-2'
            >
              {get(msg, ['startButton', 'playButtonTooltip'])}
            </Button>
            {hasSettingsPermission ? (
              <Button
                variant='secondary'
                disabled={
                  startButtonDisabled || showSettings || settingsLoading
                }
                onClick={this.handleOpenNewSettings}
                type='button'
                icon='fa-gear'
                loading={settingsLoading}
                dataTestId='snapshot-management-settings-button'
              >
                {get(msg, ['startButton', 'settings'])}
              </Button>
            ) : null}
          </div>
        ) : null}
        {snapshotRunning ? (
          <div className='relative z-0 flex items-center'>
            <Button
              type='button'
              className='mr-3'
              disabled={stopButtonsDisabled || !hasWritePermission}
              onClick={
                activeJob && this.makeHandleStopButtonClick(activeJob.get('id'))
              }
              variant='primary'
              accent='negative'
              icon='fa-circle-stop'
            >
              {isActiveJobStopping && stopButtonsDisabled
                ? get(msg, 'stoppingButtonTitle')
                : get(msg, 'stopButtonTitle')}
            </Button>
          </div>
        ) : null}
        {!newSnapshot ? (
          <div className='z-0 mr-2'>
            <Button
              variant='secondary'
              icon='fa-plug'
              id='snapshot-management-connectivity'
              onClick={this.handleConnectionReport}
              type='button'
              disabled={!(snapshotRunning || loaded)}
              size='xs'
            >
              {get(msg, ['buttonToolbar', 'connectivityReport'])}
            </Button>
          </div>
        ) : null}
        {!newSnapshot && !snapshotRunning && hasSettingsPermission && loaded ? (
          <Button
            className='mr-2'
            dataTestId='snapshot-management-settings-button'
            disabled={showSettings || settingsLoading}
            icon='fa-gear'
            loading={settingsLoading}
            onClick={this.handleOpenSettings}
            type='button'
            variant='secondary'
          >
            {get(msg, ['startButton', 'settings'])}
          </Button>
        ) : null}
        {!newSnapshot && !snapshotRunning ? (
          <div className='relative z-0 flex items-center'>
            <Button
              variant='secondary'
              className='relative mr-2'
              disabled={
                discoveryStarting ||
                isRunning ||
                isLoading ||
                loadingOrUnloading ||
                !hasWritePermission ||
                snapshotIsInJobs
              }
              id='snapshot-management-load'
              onClick={
                loaded ? this.handleUnloadSnapshots : this.handleLoadSnapshots
              }
              type='button'
              icon='fa-database'
              loading={
                loadingOrUnloading || scheduledLoading || scheduledUnloading
              }
            >
              <Icon
                glyph={loaded ? 'fa-arrow-down' : 'fa-arrow-up'}
                className='snapshots-buttons__button-icon'
                size='xs'
              />
              {get(msg, [
                'buttonToolbar',
                loaded ? 'unloadSnapshot' : 'loadSnapshot',
              ])}
            </Button>
            <OverflowMenu
              icon='fa-ellipsis-h'
              className='snapshots-buttons__more-options'
              ButtonComponent={OverflowMenuButtonLegacy}
              items={[
                {
                  label: get(msg, [
                    'buttonToolbar',
                    locked ? 'unlockSnapshot' : 'lockSnapshot',
                  ]),
                  icon: locked ? 'fa-unlock-keyhole' : 'fa-lock-keyhole',
                  loading: lockingOrUnlocking,
                  onClick: locked
                    ? this.makeHandleUnlock(id)
                    : this.makeHandleLock(id),
                  disabled:
                    discoveryStarting ||
                    isRunning ||
                    isLoading ||
                    !loaded ||
                    lockingOrUnlocking ||
                    !hasWritePermission ||
                    snapshotIsInJobs ||
                    id === null,
                },
                ...(showRunTopologyCalculation
                  ? [
                      {
                        label: get(msg, [
                          'buttonToolbar',
                          'runTopologyCalculation',
                        ]),
                        icon: 'fa-diagram-project',
                        onClick: this.makeHandleRunTopologyCalculation(id),
                        disabled: isRunTopologyCalculationDisabled({
                          loaded,
                          discoveryStarting,
                          isRunning,
                          isLoading,
                          hasWritePermission,
                          snapshotIsInJobs,
                          id,
                          topologyCalculationAlreadyRun,
                        }),
                      },
                    ]
                  : []),
                {
                  label: get(msg, ['buttonToolbar', 'downloadSnapshot']),
                  icon: 'fa-download',
                  loading: snapshotDownloadRunning,
                  onClick: this.makeHandleDownload(id),
                  disabled:
                    discoveryStarting ||
                    isRunning ||
                    isLoading ||
                    snapshotRunning ||
                    !hasWritePermission ||
                    snapshotIsInJobs ||
                    scheduledSnapshotDownload ||
                    snapshotDownloadRunning ||
                    id === null,
                },
                {
                  label: get(msg, ['buttonToolbar', 'cloneSnapshot']),
                  icon: 'fa-clone',
                  loading: cloning || scheduledClone,
                  onClick: this.makeHandleClone(id),
                  disabled:
                    discoveryStarting ||
                    isRunning ||
                    isLoading ||
                    cloning ||
                    !hasWritePermission ||
                    snapshotIsInJobs ||
                    id === null,
                },
                {
                  label: get(msg, ['buttonToolbar', 'deleteSnapshot']),
                  icon: 'fa-trash-can',
                  loading: deleting || scheduledDelete,
                  className: 'align-center',
                  accent: 'negative',
                  onClick: this.handleDeleteSnapshots,
                  disabled:
                    discoveryStarting ||
                    isRunning ||
                    isLoading ||
                    deleting ||
                    !hasWritePermission ||
                    snapshotIsInJobs,
                },
              ]}
            />
          </div>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  cloningSnapshot: makeSelectCloningSnapshot(),
  deletingSnapshot: makeSelectDeletingSnapshot(),
  discoveryIsInJobs: makeSelectDiscoveryIsInJobs(),
  discoveryRunByUser: makeSelectDiscoveryRunByUser(),
  discoveryStarting: makeSelectDiscoveryStarting(),
  isStartBtnDisabled: makeSelectDisableStartBtn(),
  loadingSnapshot: makeSelectLoadingSnapshot(),
  lockingSnapshot: makeSelectLockingSnapshot(),
  scheduledClone: makeSelectScheduledClone(),
  scheduledDelete: makeSelectScheduledDelete(),
  scheduledLoading: makeSelectScheduledLoading(),
  scheduledSnapshotDownload: makeSelectScheduledSnapshotDownload(),
  scheduledUnloading: makeSelectScheduledUnloading(),
  settingsLoading: makeSelectSettingsLoading(),
  snapshot: makeSelectContentSnapshot(),
  snapshotDownloadRunning: makeSelectSnapshotDownloadRunning(),
  snapshotIsInJobs: makeSelectSnapshotIsInJobs(),
  snapshots: makeSelectAllSnapshots(),
  unloadingSnapshot: makeSelectUnloadingSnapshot(),
  unlockingSnapshot: makeSelectUnlockingSnapshot(),
  uploadingSnapshot: makeSelectUploadingSnapshot(),
});

function mapDispatchToProps(dispatch, props) {
  return {
    onCloneSnapshot: (args) => dispatch(cloneSnapshot(args)),
    onDeleteSnapshots: (args) => dispatch(deleteSnapshot(args)),
    onDiscoveryProcessStart: (args) => dispatch(discoveryProcessStart(args)),
    onDiscoveryProcessStop: (args) => dispatch(discoveryProcessStop(args)),
    onDownloadSnapshot: (args) => dispatch(downloadSnapshot(args)),
    onLoadSnapshots: (args) => dispatch(loadSnapshot(args)),
    onLockSnapshot: (args) => dispatch(lockSnapshot(args)),
    onOpenNewSettings: (args) => dispatch(openNewSettings(args)),
    onOpenSettings: (args) => dispatch(openSettings(args)),
    onRunTopologyCalculation: (args) => dispatch(runTopologyCalculation(args)),
    onToggleConfirmDialog: (args) => dispatch(toggleConfirmDialog(args)),
    onUnloadSnapshots: (args) => dispatch(unloadSnapshot(args)),
    onUnlockSnapshot: (args) => dispatch(unlockSnapshot(args)),
    onUploadSnapshot: (args) =>
      dispatch(
        uploadSnapshot(
          merge(args, {
            currentSnapshotId: get(props, 'params.snapshotId'),
          }),
        ),
      ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Buttons);
